import React from 'react'
import {
  Datagrid,
  DeleteButton,
  EditBase,
  type Identifier,
  type InputProps,
  ListBase,
  ListToolbar,
  Loading,
  NumberInput,
  Pagination,
  type RaRecord,
  type RowClickFunction,
  SaveButton,
  SimpleForm,
  TextField,
  TextInput,
  Toolbar,
  useListContext,
  useRefresh,
  useTranslate,
  useUnselect,
  useUnselectAll
} from 'react-admin'
import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography
} from '@mui/material'
import {
  compact,
  filter,
  groupBy,
  head,
  includes,
  isEmpty,
  isNil,
  join,
  map,
  size,
  slice,
  split,
  xor
} from 'lodash/fp'
import { useFormContext } from 'react-hook-form'
import log from 'loglevel'
import { SpotList } from 'components/SpotList'
import { type ConfigProps } from 'components/Dashboard'
import { type Partner } from '@mzc-watchover/adminapp-appsync'
// import { useGetAccounts } from 'components/DataProvider'
import { OrganizationsInput } from 'components/Accounts'
import { useGetMappings } from 'components/DataProvider'
import { type Mapping as MappingType } from '../../../../cdk/bapp/lib/lambda-api-v2/src/types'
import { useNotify } from 'components/Util'

log.setLevel((process.env['LOG_LEVEL'] as log.LogLevelDesc) ?? log.levels.INFO)

const SelectAccountInput: React.FC<
  InputProps &
    ConfigProps & {
      multiple?: boolean
      mappings: MappingType[]
    }
> = (props) => {
  const { source, multiple, config, mappings } = props
  const { Mappings: configMapping, Mappings: configMappings } = config
  const [selectAccount, setSelectAccount] = React.useState<{
    managedBy: boolean
    ownedBy: boolean
  }>({ managedBy: false, ownedBy: false })
  const translate = useTranslate()
  const notify = useNotify()
  const { setValue, getValues } = useFormContext()
  const [selected, setSelected] = React.useState<RaRecord[] | undefined>(
    undefined
  )
  const unselectAll = useUnselectAll('accounts')
  const unselect = useUnselect('accounts')
  const onSelectClick = (): void => {
    const item = JSON.parse(JSON.stringify(selectAccount))
    item[source] = true
    setSelectAccount(item)
  }
  const item = getValues()[source]
  const MappingSelectAccountDialogContent: React.FC = () => {
    const { isLoading, isFetching, selectedIds, data, onSelect } =
      useListContext()
    const { setValue } = useFormContext()
    React.useEffect(() => {
      if (isNil(isFetching) || isFetching) {
        if (!isNil(selected)) setSelected(undefined)
        return
      }
      if (isNil(isLoading) || isLoading || !isNil(selected)) {
        return
      }
      const currents = compact(split(',')(item))
      if (isEmpty(currents)) {
        setSelected([])
        return
      }
      const byName = groupBy<RaRecord>('name')(data)
      const items = compact(map((name: string) => head(byName[name]))(currents))
      if (isEmpty(items)) {
        setSelected([])
        return
      }
      onSelect(map((item: RaRecord) => item.id)(items))
      setSelected(items)
    }, [isFetching, isLoading, item, data, selected])
    React.useEffect(() => {
      if (isNil(selected)) return
      const currentIds = map((item: RaRecord) => item.id)(selected)
      const displayed = filter((item: RaRecord) =>
        includes(item.id)(selectedIds)
      )(data)
      const others = filter(
        (item: RaRecord) =>
          !includes(item.id)(map((item: RaRecord) => item.id)(data))
      )(selected)
      const all = [...displayed, ...others]
      const diff = xor(map((item: RaRecord) => item.id)(all))(currentIds)
      // log.info({
      //   selected,
      //   selectedIds,
      //   all,
      //   diff
      // })
      if (!isEmpty(diff)) setSelected(all)
    }, [selectedIds, selected, data])
    const { name } = getValues()
    const { name: nameKey } = configMappings
    React.useEffect(() => {
      if (isNil(isLoading) || isLoading) return
      const users = filter((item: RaRecord) => includes('user')(item['group']))(
        selected
      )
      if (isEmpty(users)) return
      const byManagedBy = groupBy<MappingType>('managedBy')(mappings)
      const wrongUsers = filter(
        (user: RaRecord) =>
          !isNil(byManagedBy[user['name']]) &&
          !includes(name)(
            map((item: MappingType) => item.name)(byManagedBy[user['name']])
          )
      )(users)
      if (isEmpty(wrongUsers)) return
      notify(
        translate('asigned users', {
          name: nameKey,
          users: join(',')(map((item: RaRecord) => item['name'])(wrongUsers))
        })
      )
      unselect(map((item: RaRecord) => item.id)(wrongUsers))
    }, [name, selected, isLoading, mappings, nameKey])
    const handleRowClick: RowClickFunction = (
      _id: Identifier,
      _resource: string,
      record: RaRecord
    ): any => {
      if (isNil(multiple) || !(multiple as boolean)) {
        setValue(source, record['name'], {
          shouldDirty: true,
          shouldValidate: true
        })
        const item = JSON.parse(JSON.stringify(selectAccount))
        item[source] = false
        setSelectAccount(item)
        return false
      } else {
        // if (isNil(selected)) return
        // const isSelect = isNil(
        //   find((item: string) => item === id)(selectedIds)
        // )
        // setRowClick(true)
        // if (isSelect) {
        //   setSelected([...selected, record])
        // } else {
        //   setSelected(filter((item: RaRecord) => item.id !== id)(selected))
        // }
        return 'toggleSelection'
      }
    }

    if (!isNil(isLoading) && isLoading) return <Loading />
    return (
      <Datagrid
        bulkActionButtons={
          isNil(multiple) || !(multiple as boolean) ? false : <></>
        }
        rowClick={handleRowClick}>
        <TextField source="name" sx={{ display: 'block', width: 200 }} />
        <TextField source="email" sx={{ display: 'block', width: 300 }} />
      </Datagrid>
    )
  }
  // const dialog = React.useMemo(() => {
  const onCancel = (): void => {
    const item = JSON.parse(JSON.stringify(selectAccount))
    item[source] = false
    setSelectAccount(item)
    unselectAll()
    setSelected(undefined)
  }
  const onClick = (): void => {
    const items = join(',')(map((item: RaRecord) => item['name'])(selected))
    setValue(source, items, {
      shouldDirty: true,
      shouldValidate: true
    })
    const item = JSON.parse(JSON.stringify(selectAccount))
    item[source] = false
    setSelectAccount(item)
    setSelected(undefined)
  }
  const onClear = (): void => {
    setValue(source, '', {
      shouldDirty: true,
      shouldValidate: true
    })
    const item = JSON.parse(JSON.stringify(selectAccount))
    item[source] = false
    setSelectAccount(item)
    setSelected(undefined)
  }
  const handleClose =
    (source: string): (() => void) =>
    () => {
      const item = JSON.parse(JSON.stringify(selectAccount))
      item[source] = false
      setSelectAccount(item)
      setSelected(undefined)
    }
  const items = split(',')(item)
  return (
    <>
      <Typography>
        {configMapping.accounts ??
          translate(`resources.mappings.fields.${source as string}`)}
        {!isNil(item) && !isEmpty(item) && (
          <Typography variant="caption">
            (
            {translate(
              `resources.mappings.fields.${source as string}.helperTextEdit`
            )}
            )
          </Typography>
        )}
      </Typography>
      <Chip
        onClick={onSelectClick}
        label={
          !isNil(item) && !isEmpty(item)
            ? !isNil(multiple) && (multiple as boolean)
              ? `${join(', ')(slice(0, 3)(items))}${
                  size(items) > 3 ? '....' : ''
                }`
              : item
            : translate(
                `resources.mappings.fields.${source as string}.helperText`
              )
        }
      />
      {(selectAccount[source] as boolean) && (
        <Dialog
          onClose={handleClose(source)}
          open={selectAccount[source]}
          maxWidth="md"
          fullWidth>
          <DialogTitle>
            {(source === 'managedBy'
              ? configMapping.selectAccountDialog
              : configMapping.selectOwnerAccountDialog) ??
              `${translate(
                `resources.mappings.dialogs.${
                  source === 'managedBy'
                    ? 'selectAccount'
                    : 'selectOwnerAccount'
                }.title`
              )}`}
          </DialogTitle>
          <DialogContent>
            <ListBase
              resource="accounts"
              filter={{
                group: source === 'managedBy' ? ['manager', 'user'] : 'owner'
              }}
              disableSyncWithLocation>
              <ListToolbar
                filters={[<TextInput source="name" alwaysOn resettable />]}
              />
              <Card>
                <MappingSelectAccountDialogContent />
                <Pagination />
              </Card>
            </ListBase>
          </DialogContent>
          <DialogActions
            sx={{ justifyContent: 'flex-start', ml: '1em', mt: '-1em' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%'
              }}>
              <Box>
                <Button variant="outlined" onClick={onCancel}>
                  {translate(
                    `resources.mappings.dialogs.selectAccount.buttons.cancel`
                  )}
                </Button>
                <Button
                  variant="contained"
                  onClick={onClick}
                  sx={{ ml: '1em' }}>
                  {translate(
                    `resources.mappings.dialogs.selectAccount.buttons.done`
                  )}
                </Button>
              </Box>
              <Button variant="contained" onClick={onClear}>
                {translate(
                  `resources.mappings.dialogs.selectAccount.buttons.clear`
                )}
              </Button>
            </Box>
          </DialogActions>
        </Dialog>
      )}
    </>
  )
}

const MappingMutateFormContent: React.FC<
  { record: any; partner: string } & ConfigProps
> = ({ config, record, partner }) => {
  const { Mapping: configMapping } = config
  const { getValues } = useFormContext()
  const { code: codeField } = configMapping.fields
  const { data: mappings, isLoading } = useGetMappings(partner)
  const { spin } = getValues() as {
    spin: string
  }
  const { tenant } = record ?? { tenant: undefined }
  if ((isLoading as boolean) || isNil(mappings)) return null
  return (
    <>
      <TextInput source="name" fullWidth readOnly />
      <TextInput source="spin" fullWidth readOnly={size(spin) === 22} />
      <TextInput
        source={codeField.source}
        label={codeField.label}
        // readOnly={!isEmpty(getValues()[codeField.source])}
      />
      {!isNil(tenant) && (tenant as boolean) && (
        <OrganizationsInput config={config} partner={partner} />
      )}
      <NumberInput source="floorArea" fullWidth min={0} />
      <TextInput source="group" fullWidth />
      <SelectAccountInput
        source="managedBy"
        multiple
        mappings={mappings}
        config={config}
      />
      {/* <SelectAccountInput source="ownedBy" /> */}
    </>
  )
}

export const Mapping: React.FC<ConfigProps & { partner: Partial<Partner> }> = ({
  config,
  partner
}) => {
  const { Mapping: configMapping } = config
  const translate = useTranslate()
  // const notify = useNotify()
  // const { data: mappings, isLoading } = useGetMappings(partner.id as string)
  const [mutate, setMutate] = React.useState(false)
  const [mode, setMode] = React.useState<string | undefined>(undefined)
  const [record, setRecord] = React.useState<RaRecord | undefined>(undefined)
  // const { isLoading: isLoadingAccounts, data: accounts } = useGetAccounts(
  //   partner.id as string
  // )
  const refresh = useRefresh()
  const handleClose = (): void => {
    setMutate(false)
    refresh()
  }
  const handleRowClick: RowClickFunction = (
    _id: Identifier,
    _resource: string,
    record: RaRecord
  ) => {
    setMode('edit')
    setMutate(true)
    setRecord(record)
    return false
  }
  const MappingMutateToolbar: React.FC = () => {
    const { tenant } = record ?? { tenant: undefined }
    return (
      <Toolbar>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%'
          }}>
          <Box>
            <Button variant="outlined" onClick={handleClose}>
              {translate('resources.mappings.dialogs.mutate.buttons.cancel')}
            </Button>
            <SaveButton
              label="resources.mappings.dialogs.mutate.buttons.done"
              sx={{ ml: '1em' }}
            />
          </Box>
          {!isNil(tenant) && (tenant as boolean) && (
            <DeleteButton
              label="resources.mappings.dialogs.mutate.buttons.delete"
              mutationMode="pessimistic"
              mutationOptions={{
                onSuccess: () => {
                  setMutate(false)
                }
              }}
              variant="outlined"
              size="large"
              confirmTitle="resources.mappings.dialogs.mutate.confirm.title"
              confirmContent="resources.mappings.dialogs.mutate.confirm.content"
              redirect={false}
            />
          )}
        </Box>
      </Toolbar>
    )
  }
  const { mutateDialog } = configMapping
  const dialog = React.useMemo(() => {
    return (
      <Dialog onClose={handleClose} open={mutate} maxWidth="md" fullWidth>
        <DialogTitle>
          {mode === 'edit'
            ? mutateDialog ??
              translate(`resources.mappings.dialogs.${mode as string}.title`)
            : ''}
        </DialogTitle>
        <DialogContent>
          {mode === 'edit' && !isNil(record) && (
            <EditBase
              id={record.id}
              resource="mappings"
              redirect={false}
              mutationMode="pessimistic"
              mutationOptions={{
                onSuccess: () => {
                  handleClose()
                }
              }}>
              <SimpleForm toolbar={<MappingMutateToolbar />}>
                <MappingMutateFormContent
                  config={config}
                  record={record}
                  partner={partner.id as string}
                />
              </SimpleForm>
            </EditBase>
          )}
        </DialogContent>
      </Dialog>
    )
  }, [mutate, handleClose, mode, mutateDialog, record])
  // const { managedBy } = configMapping
  // if (isLoading) return <Loading />
  return (
    <>
      <SpotList
        resource="mappings"
        // items={[
        //   <FunctionField
        //     key="managedBy"
        //     label={managedBy ?? 'resources.mappings.fields.managedBy'}
        //     render={(record: { managedBy: { name: string; email: string } }) =>
        //       !isNil(record.managedBy)
        //         ? `${record.managedBy.name} (${record.managedBy.email})`
        //         : ''
        //     }
        //     sx={{ display: 'block', width: 400 }}
        //   />
        // ]}
        // additionalFilters={[
        //   <TextInput
        //     source="managedBy.name"
        //     label="resources.mappings.fields.managedBy"
        //     alwaysOn
        //     resettable
        //   />
        // ]}
        rowClick={handleRowClick}
        config={config}
        partner={partner}
        perPage={50}
      />
      {mutate && !isNil(mode) && dialog}
    </>
  )
}
